import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { X, Printer } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  player_name: "",
  dob: "",
  season: "2027",
  parent_name: "",
  phone: "",
  email: "",
  emergency_contact_name: "",
  emergency_relationship: "",
  emergency_phone: "",
  allergies: false,
  allergies_detail: "",
  medications: false,
  medications_detail: "",
  conditions: false,
  conditions_detail: "",
  injuries: false,
  injuries_detail: "",
  restrictions: false,
  restrictions_detail: "",
  other_notes: "",
  emergency_first_contact: "parent",
  emergency_first_contact_other: "",
  emergency_care_permission: false,
  accuracy_certification: false,
  signed_name: "",
};

function YesNo({ value, onChange, name, testId }) {
  return (
    <div className="flex gap-2">
      {[["yes", true], ["no", false]].map(([label, val]) => (
        <label
          key={label}
          className={`flex-1 cursor-pointer border rounded-md py-2 text-center text-sm uppercase tracking-widest ${
            value === val
              ? "bg-[#00E5FF]/15 border-[#00E5FF] text-[#00E5FF]"
              : "bg-[#050A15] border-white/10 text-slate-400 hover:border-white/30"
          }`}
        >
          <input
            type="radio"
            name={name}
            checked={value === val}
            onChange={() => onChange(val)}
            className="sr-only"
            data-testid={`${testId}-${label}`}
          />
          {label}
        </label>
      ))}
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-[#00E5FF] mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

const INPUT_CLS = "w-full bg-[#050A15] border border-white/10 rounded-md p-2.5 text-sm";

export default function MedicalFormModal({ onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previouslySaved, setPreviouslySaved] = useState(false);

  useEffect(() => {
    api
      .get("/medical-form/mine")
      .then((r) => {
        if (r.data && r.data.player_name !== undefined && Object.keys(r.data).length > 0) {
          setForm({ ...EMPTY, ...r.data });
          setPreviouslySaved(true);
        } else {
          setForm({
            ...EMPTY,
            player_name: user?.player_name || "",
            parent_name: user?.name || "",
            phone: user?.phone || "",
            email: user?.email || "",
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.emergency_care_permission || !form.accuracy_certification) {
      toast.error("Please confirm both agreements at the bottom of the form.");
      return;
    }
    if (!form.signed_name.trim()) {
      toast.error("Please type your full name as parent/guardian signature.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/medical-form", form);
      toast.success(previouslySaved ? "Medical form updated." : "Medical form submitted.");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const printForm = () => window.print();

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={() => !submitting && onClose()}
      data-testid="medical-form-modal"
    >
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <div
          onClick={(e) => e.stopPropagation()}
          className="kr-card rounded-2xl p-6 sm:p-8 max-w-3xl w-full my-8 relative"
        >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white"
          data-testid="medical-form-close"
        >
          <X size={20} />
        </button>
        <div className="eyebrow mb-1">Required Form</div>
        <h2 className="font-display text-2xl uppercase mb-1">Medical Information Form</h2>
        <p className="text-xs text-slate-400 mb-6">
          Please complete this form so Coastal Kraken coaches can respond appropriately in case of injury or
          medical issue. All information is kept confidential and used by team staff only.
        </p>

        {loading ? (
          <div className="py-12 text-center text-slate-500">Loading…</div>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            {/* Player & Parent */}
            <section className="space-y-3">
              <div className="eyebrow">Player & Parent Information</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Player Name" required>
                  <input required value={form.player_name} onChange={(e) => set({ player_name: e.target.value })} className={INPUT_CLS} data-testid="med-player-name" />
                </Field>
                <Field label="Date of Birth" required>
                  <input required type="date" value={form.dob} onChange={(e) => set({ dob: e.target.value })} className={INPUT_CLS} data-testid="med-dob" />
                </Field>
                <Field label="Season">
                  <input value={form.season} onChange={(e) => set({ season: e.target.value })} className={INPUT_CLS} placeholder="e.g. 2027" />
                </Field>
                <Field label="Parent / Guardian Name" required>
                  <input required value={form.parent_name} onChange={(e) => set({ parent_name: e.target.value })} className={INPUT_CLS} data-testid="med-parent-name" />
                </Field>
                <Field label="Best Contact Phone" required>
                  <input required value={form.phone} onChange={(e) => set({ phone: e.target.value })} className={INPUT_CLS} />
                </Field>
                <Field label="Email" required>
                  <input required type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className={INPUT_CLS} />
                </Field>
              </div>
            </section>

            {/* Emergency Contact */}
            <section className="space-y-3">
              <div className="eyebrow">Emergency Contact (other than parent above)</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Name">
                  <input value={form.emergency_contact_name} onChange={(e) => set({ emergency_contact_name: e.target.value })} className={INPUT_CLS} />
                </Field>
                <Field label="Relationship">
                  <input value={form.emergency_relationship} onChange={(e) => set({ emergency_relationship: e.target.value })} className={INPUT_CLS} placeholder="e.g. Grandparent" />
                </Field>
                <Field label="Phone">
                  <input value={form.emergency_phone} onChange={(e) => set({ emergency_phone: e.target.value })} className={INPUT_CLS} />
                </Field>
              </div>
            </section>

            {/* Medical Yes/No */}
            <section className="space-y-4">
              <div className="eyebrow">Medical History</div>
              {[
                ["allergies", "allergies_detail", "Does your player have any known allergies (food, insect, medication, environmental)?", "List allergies and reactions"],
                ["medications", "medications_detail", "Does your player take any daily or as-needed medications?", "List medications and dosage"],
                ["conditions", "conditions_detail", "Does your player have any medical conditions we should be aware of (asthma, diabetes, heart, seizure, etc.)?", "List conditions"],
                ["injuries", "injuries_detail", "Any recent injuries, surgeries, or ongoing rehab in the past 12 months?", "Describe injury / treatment / status"],
                ["restrictions", "restrictions_detail", "Are there any physical activity restrictions or limitations?", "Describe restrictions"],
              ].map(([flag, detail, question, placeholder]) => (
                <div key={flag} className="bg-[#050A15] border border-white/10 rounded-md p-4 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-sm text-slate-200 flex-1">{question}</p>
                    <div className="w-40 shrink-0">
                      <YesNo
                        value={form[flag]}
                        onChange={(v) => set({ [flag]: v })}
                        name={flag}
                        testId={`med-${flag}`}
                      />
                    </div>
                  </div>
                  {form[flag] && (
                    <textarea
                      required
                      value={form[detail]}
                      onChange={(e) => set({ [detail]: e.target.value })}
                      placeholder={placeholder}
                      rows={2}
                      className={INPUT_CLS}
                      data-testid={`med-${flag}-detail`}
                    />
                  )}
                </div>
              ))}

              <Field label="Other notes for coaches (insurance carrier, doctor's name, special instructions, etc.)">
                <textarea
                  value={form.other_notes}
                  onChange={(e) => set({ other_notes: e.target.value })}
                  rows={3}
                  className={INPUT_CLS}
                  placeholder="Anything else our staff should know"
                />
              </Field>
            </section>

            {/* Emergency preference */}
            <section className="space-y-3">
              <div className="eyebrow">In Case of Emergency</div>
              <Field label="Who should team staff contact first?">
                <div className="grid sm:grid-cols-3 gap-2">
                  {[
                    ["parent", "Parent listed above"],
                    ["emergency", "Emergency contact"],
                    ["other", "Other (specify)"],
                  ].map(([val, label]) => (
                    <label
                      key={val}
                      className={`cursor-pointer border rounded-md py-2 px-3 text-sm text-center ${
                        form.emergency_first_contact === val
                          ? "bg-[#00E5FF]/15 border-[#00E5FF] text-[#00E5FF]"
                          : "bg-[#050A15] border-white/10 text-slate-300 hover:border-white/30"
                      }`}
                    >
                      <input
                        type="radio"
                        name="first_contact"
                        checked={form.emergency_first_contact === val}
                        onChange={() => set({ emergency_first_contact: val })}
                        className="sr-only"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </Field>
              {form.emergency_first_contact === "other" && (
                <input
                  required
                  value={form.emergency_first_contact_other}
                  onChange={(e) => set({ emergency_first_contact_other: e.target.value })}
                  placeholder="Name and phone of preferred first contact"
                  className={INPUT_CLS}
                />
              )}
            </section>

            {/* Agreements */}
            <section className="space-y-3">
              <div className="eyebrow">Agreements & Signature</div>
              <label className="flex items-start gap-3 text-sm text-slate-200 cursor-pointer">
                <input
                  type="checkbox"
                  required
                  checked={form.emergency_care_permission}
                  onChange={(e) => set({ emergency_care_permission: e.target.checked })}
                  className="mt-1"
                  data-testid="med-emergency-permission"
                />
                <span>
                  In the event of a medical emergency where I cannot be reached, I authorize Coastal Kraken
                  Baseball staff to seek and approve appropriate emergency medical care for my child.
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm text-slate-200 cursor-pointer">
                <input
                  type="checkbox"
                  required
                  checked={form.accuracy_certification}
                  onChange={(e) => set({ accuracy_certification: e.target.checked })}
                  className="mt-1"
                  data-testid="med-accuracy"
                />
                <span>
                  I certify that the information provided above is accurate and complete to the best of my
                  knowledge. I will update Coastal Kraken Baseball promptly if anything changes during the season.
                </span>
              </label>

              <Field label="Parent / Guardian Typed Signature" required>
                <input
                  required
                  value={form.signed_name}
                  onChange={(e) => set({ signed_name: e.target.value })}
                  className={INPUT_CLS}
                  placeholder="Type your full legal name"
                  data-testid="med-signature"
                />
              </Field>
              <div className="text-xs text-slate-400">
                Date: <span className="text-white">{new Date().toLocaleDateString()}</span>
              </div>
            </section>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
              <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">
                Cancel
              </button>
              {previouslySaved && (
                <button type="button" onClick={printForm} className="btn-ghost text-sm">
                  <Printer size={14} /> Print
                </button>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary flex-1 justify-center"
                data-testid="med-submit"
              >
                {submitting ? "Saving…" : previouslySaved ? "Update Medical Form" : "Submit Medical Form"}
              </button>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
}
